import { Controller } from "@hotwired/stimulus"
import * as L from 'leaflet';
import { get } from '@rails/request.js'
import 'leaflet.markercluster';
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["map", "account", "group", 'competitor', 'legend', 'filterRow']
  static values = {
    freeForm: {type: Boolean, default: false},
    trainingMode: {type: Boolean, default: false},
    activeFilters: {type: Array, default: []},
    selectedAccount: Number,
    selectedCompetitor: Number,
    selectedCompetitors: Array,
    visibleAccounts: Array,
    lastLng: Number,
    lastLat: Number,
    lastZoom: Number,
    radius: {type: Number, default: 80467},
    oldSelectedAccount: Number,
  }

  selectedAccounts = new Set();
  selectedGroups = new Set();
  markersById = {};

  map; mapBounds; geoJSONLayer; selectedAccountLayer; accountsLayer; competitorsLayer; drawnCircle; lastClickedLayer;

  accountSelectionBox = document.querySelector('#account-selection-box')
  selectedAccountBox = document.querySelector('#selected-account-box')
  selectedAccountBoxUrl = document.querySelector("#selected_account")
  selectedCompetitorBox = document.querySelector('#selected-competitor-box')
  selectedCompetitorBoxUrl = document.querySelector("#selected_competitor")
  accountMap = document.querySelector("#account_map")
  accountInfo = document.querySelector("#account_info")

  connect() {
    this.initializeIcons()
    this.setupMap()

    this.channel = createConsumer().subscriptions.create("MapUpdatesChannel", {
      received: this.handleRealTimeUpdate.bind(this)
    })
  }

  disconnect() {
    if (this.channel) this.channel.unsubscribe()
  }

  // --- Initialize Icons safely after the controller connects ---
  initializeIcons() {
    this.redIcon = this.createIcon('red')
    this.yellowIcon = this.createIcon('yellow')
    this.greenIcon = this.createIcon('green')
    this.blueIcon = this.createIcon('blue')
    this.orangeIcon = this.createIcon('orange')
    this.purpleIcon = this.createIcon('purple')
  }


  createIcon(color) {
    // Explicit mappings prevent Tailwind JIT compiler from purging dynamic classes
    const styles = {
      red: { bg: 'bg-red-500', border: 'border-t-red-500' },
      yellow: { bg: 'bg-yellow-500', border: 'border-t-yellow-500' },
      green: { bg: 'bg-green-500', border: 'border-t-green-500' },
      blue: { bg: 'bg-blue-500', border: 'border-t-blue-500' },
      orange: { bg: 'bg-orange-500', border: 'border-t-orange-500' },
      purple: { bg: 'bg-purple-500', border: 'border-t-purple-500' }
    };
    const style = styles[color] || styles.blue;

    return L.divIcon({
      className: `custom-marker ${color}-marker`,
      html: `<div class="marker-content"><div class="marker-circle ${style.bg}"></div><div class="marker-triangle ${style.border}"></div></div>`,
      iconSize: [32, 48],
    })
  }

  getIconInstance(color) {
    if (color === 'red') return this.redIcon;
    if (color === 'yellow') return this.yellowIcon;
    if (color === 'green') return this.greenIcon;
    if (color === 'orange') return this.orangeIcon;
    if (color === 'purple') return this.purpleIcon;
    return this.blueIcon;
  }

  setupMap() {
    let startLat = this.lastLatValue || 35.955615358406504
    let startLng = this.lastLngValue || -80.00355095602737
    let startZoom = this.lastZoomValue || 13

    this.map = L.map(this.mapTarget, {preferCanvas: true}).setView([startLat, startLng], startZoom);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      minZoom: 4,
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.map);

    this.map.on("moveend", () => {
      this.updateLocation()
      this.updateAccountList()
    })

    this.selectedAccountLayer = L.layerGroup().addTo(this.map);
    this.competitorsLayer = L.layerGroup().addTo(this.map);

    this.map.on('click', () => {
      if (this.drawnCircle) this.map.removeLayer(this.drawnCircle);
    })
  }

  // --- Real-Time Update Handler ---
  handleRealTimeUpdate(data) {
    const marker = this.markersById[data.location_id]
    if (marker && this.trainingModeValue) {
      let newIcon = this.orangeIcon;
      if (data.new_state === 'red') newIcon = this.redIcon;
      if (data.new_state === 'yellow') newIcon = this.yellowIcon;
      if (data.new_state === 'green') newIcon = this.greenIcon;
      if (data.new_state === 'purple') newIcon = this.purpleIcon;
      marker.setIcon(newIcon)
    }
  }

  // --- Toggle Actions ---
  async toggleTrainingMode() {
    this.trainingModeValue = !this.trainingModeValue

    // Show/Hide legend based on training mode
    if (this.hasLegendTarget) {
      this.legendTarget.classList.toggle('hidden', !this.trainingModeValue)
    }

    if (!this.trainingModeValue) {
      this.activeFiltersValue = [];
      this.updateFilterUI();
    }
    await this.updateLocation()
  }

  toggleFilter(event) {
    const color = event.currentTarget.dataset.color;
    let currentFilters = [...this.activeFiltersValue];

    if (currentFilters.includes(color)) {
      currentFilters = currentFilters.filter(c => c !== color);
    } else {
      currentFilters.push(color);
    }

    this.activeFiltersValue = currentFilters;
    this.updateFilterUI();
    this.updateLocation(); // Re-render the map
  }

  updateFilterUI() {
    this.filterRowTargets.forEach(row => {
      const color = row.dataset.color;
      if (this.activeFiltersValue.length === 0 || this.activeFiltersValue.includes(color)) {
        row.classList.remove('opacity-30');
      } else {
        row.classList.add('opacity-30');
      }
    });
  }

  async freeFormToggle() {
    this.freeFormValue = !this.freeFormValue
    await this.updateLocation()
    this.updateAccountList()
  }

  // --- Map Fetching & Rendering ---
  async updateLocation() {
    const bounds = this.map.getBounds()
    const center = bounds.getCenter()
    const bound_box = bounds.toBBoxString()

    const response = await get("map/location/data", {responseKind: "json", query: {
      center_lat: center.lat,
      center_lng: center.lng,
      zoom: this.map.getZoom(),
      bound_box: (this.freeFormValue ? bound_box : ''),
      other_account_ids: this.selectedCompetitorsValue,
      account_id: this.selectedAccountValue,
      free_form: this.freeFormValue,
      training_mode: this.trainingModeValue
    }})

    if (response.ok) {
      const data = await response.json
      this.markersById = {} // Reset marker tracker

      // Set up Dynamic Clusters
      let globalCluster = L.markerClusterGroup({ iconCreateFunction: this.createDynamicCluster.bind(this, 'blue') });
      let selectedCluster = L.markerClusterGroup({ iconCreateFunction: this.createDynamicCluster.bind(this, 'orange') });

      // Core Filter Logic: Only applies if Training Mode is ON
      const filterFeature = (feature) => {
        if (!this.trainingModeValue) return true;
        if (this.activeFiltersValue.length === 0) return true;
        return this.activeFiltersValue.includes(feature.properties.visit_state);
      }

      // Core Coloring Logic: Strict blue/orange unless Training Mode is ON
      const pointToLayer = (feature, latlng, isSelectedGroup) => {
        let color = isSelectedGroup ? 'orange' : 'blue';

        if (this.trainingModeValue) {
          const state = feature.properties.visit_state;
          if (state === 'red') color = 'red';
          else if (state === 'yellow') color = 'yellow';
          else if (state === 'green') color = 'green';
          else if (state === 'purple') color = 'purple';
        }

        const icon = this.getIconInstance(color);
        const marker = L.marker(latlng, { icon: icon, assignedColor: color }); // Save color for cluster logic
        this.markersById[feature.properties.id] = marker;
        return marker;
      }

      // 1. Render Global / Other Locations
      L.geoJSON(data.locations, {
        filter: filterFeature,
        onEachFeature: this.onEachFeature,
        pointToLayer: (feature, latlng) => pointToLayer(feature, latlng, false)
      }).eachLayer((layer) => {
        globalCluster.addLayer(layer)
        this.addCircleToMap(layer)
      });

      // 2. Render Selected Account Locations
      L.geoJSON(data.selected_locations, {
        filter: filterFeature,
        onEachFeature: this.onEachFeature,
        pointToLayer: (feature, latlng) => pointToLayer(feature, latlng, true)
      }).eachLayer((layer) => {
        selectedCluster.addLayer(layer)
        this.addCircleToMap(layer)
      });

      // 3. Clear Old Layers & Mount New Ones
      if (this.selectedAccountLayer) this.map.removeLayer(this.selectedAccountLayer);
      this.selectedAccountLayer = selectedCluster;
      this.map.addLayer(selectedCluster);

      if (this.competitorsLayer) this.map.removeLayer(this.competitorsLayer);
      this.competitorsLayer = globalCluster;
      this.map.addLayer(globalCluster);

      // Handle Visibility Lists
      this.visibleAccountsValue = ((this.freeFormValue || this.trainingModeValue) && Array.isArray(data.accounts)) ? data.accounts : [];

      if(this.selectedAccountValue != this.oldSelectedAccountValue){
        this.setBoundsForSelectedAccount(data);
        this.oldSelectedAccountValue = this.selectedAccountValue
      }
    }
  }

  createDynamicCluster(defaultFallbackColor, cluster) {
    const markers = cluster.getAllChildMarkers();
    let finalColor = defaultFallbackColor;

    // In training mode, a single red pin makes the whole cluster bubble red.
    if (this.trainingModeValue) {
      const colors = markers.map(m => m.options.assignedColor);
      if (colors.includes('red')) finalColor = 'red';
      else if (colors.includes('yellow')) finalColor = 'yellow';
      else if (colors.includes('green')) finalColor = 'green';
      else if (colors.includes('purple')) finalColor = 'purple';
    }

    // Explicit Tailwind mappings for clusters
    const styles = {
      red: { bg: 'bg-red-500', op: 'bg-red-400' },
      yellow: { bg: 'bg-yellow-500', op: 'bg-yellow-400' },
      green: { bg: 'bg-green-500', op: 'bg-green-400' },
      blue: { bg: 'bg-blue-500', op: 'bg-blue-400' },
      orange: { bg: 'bg-orange-500', op: 'bg-orange-400' },
      purple: { bg: 'bg-purple-500', op: 'bg-purple-400' }
    };
    const style = styles[finalColor] || styles.blue;

    return L.divIcon({
      html: `<div class='grid place-content-center h-8 w-8 relative'>
        <div class="absolute inset-0 rounded-full h-8 w-8 inline-flex ${style.op} opacity-30"></div>
        <div class="absolute inset-1 rounded-full h-6 w-6 inline-flex ${style.op} opacity-70"></div>
        <div class="absolute inset-2 rounded-full h-4 w-4 grid place-content-center ${style.bg} text-white text-xs font-bold shadow">${cluster.getChildCount()}</div>
      </div>`,
      className: 'custom-cluster-icon',
      iconSize: L.point(40, 40),
    });
  }

  priorityClusterIcon(cluster) {
    const markers = cluster.getAllChildMarkers();
    let highestPriorityColor = 'orange'; // Fallback

    // Check states of all markers inside this specific cluster
    let states = markers.map(m => m.options.visitState);

    if (states.includes('red')) {
      highestPriorityColor = 'red';
    } else if (states.includes('purple')) {
      highestPriorityColor = 'purple';
    } else if (states.includes('yellow')) {
      highestPriorityColor = 'yellow';
    } else if (states.includes('green')) {
      highestPriorityColor = 'green';
    }

    return L.divIcon({
        html: `<div class='grid place-content-center h-8 w-8 relative'>
          <div class="absolute inset-0 rounded-full h-8 w-8 inline-flex bg-${highestPriorityColor}-400 opacity-20"></div>
          <div class="absolute inset-1 rounded-full h-6 w-6 inline-flex bg-${highestPriorityColor}-400 opacity-60"></div>
          <div class="absolute inset-2 rounded-full h-4 w-4 grid place-content-center bg-${highestPriorityColor}-600 text-white text-xs font-bold shadow">${cluster.getChildCount()}</div>
        </div>`,
        className: 'custom-cluster-icon',
        iconSize: L.point(40, 40),
    });
  }

  getIconForFeature(feature, isSelectedGroup) {
    // 1. If it has a specific training state (Red/Yellow/Green/Purple), ALWAYS show it.
    if (feature.properties.visit_state === 'red') return this.redIcon;
    if (feature.properties.visit_state === 'yellow') return this.yellowIcon;
    if (feature.properties.visit_state === 'green') return this.greenIcon;
    if (feature.properties.visit_state === 'purple') return this.purpleIcon;

    // 2. Fallbacks for standard locations (no training state)
    return isSelectedGroup ? this.orangeIcon : this.blueIcon;
  }

  // --- Helper Methods ---

  // Arrow function to auto-bind 'this'
  onEachFeature = (feature, layer) => {
    if (feature.properties && feature.properties.popupContent) {
      layer.bindPopup(feature.properties.popupContent);
    }
  }

  clusterIcon(color) {
    return function (cluster) {
      return L.divIcon({
          html: `<div class='grid place-content-center h-8 w-8 relative'>
            <div class="absolute inset-0 rounded-full h-8 w-8 inline-flex bg-${color}-400 opacity-20"></div>
            <div class="absolute inset-1 rounded-full h-6 w-6 inline-flex bg-${color}-400 opacity-50"></div>
            <div class="absolute inset-2 rounded-full h-4 w-4 grid place-content-center bg-${color}-400 font-medium">${cluster.getChildCount()}</div>
          </div>`,
          className: 'custom-cluster-icon',
          iconSize: L.point(40, 40),
      });
    };
  }

  addCircleToMap(layer){
    layer.on('click', (e) => {
      if (this.drawnCircle) this.map.removeLayer(this.drawnCircle);
      this.drawnCircle = L.circle(e.latlng, {
        radius: this.radiusValue, color: 'blue', fillColor: '#3388ff', fillOpacity: 0.1, weight: 0.5
      }).addTo(this.map);
    })
  }

  adjustRadius(event) {
    this.radiusValue = event.target.value * 1609.344
    if (this.drawnCircle) {
      this.map.removeLayer(this.drawnCircle);
      this.drawnCircle = L.circle(this.drawnCircle._latlng, {
        radius: this.radiusValue, color: 'blue', fillColor: '#3388ff', fillOpacity: 0.1, weight: 0.5
      }).addTo(this.map);
    }
    const label = document.querySelector('#circle-radius-label')
    if(label) label.innerHTML = `${event.target.value} - Radius(mi)`
  }

  deselectAccount() {
    this.selectedAccountValue = 0
    this.accountSelectionBox.hidden = false
    this.selectedAccountBox.hidden = true
    this.selectedCompetitorsValue = []
    this.updateLocation()
  }

  async selectAccount(event) {
    this.selectedAccountValue = event.target.dataset.id
    this.accountSelectionBox.hidden = true
    this.selectedAccountBox.hidden = false
    this.updateSelectedAccountUrl()
    this.updateLocation()
  }

  accountSelect(event){
    this.toggleSelected(event.target, 'account')
  }

  async selectCompetitor(event){
    this.selectedCompetitorValue = event.target.dataset.id
    this.selectedCompetitorsValue = [this.selectedCompetitorValue]
    this.selectedAccountBox.hidden = true
    this.selectedCompetitorBox.hidden = false
    this.updateSelectedCompetitorUrl()
    this.updateLocation()
  }

  deselectCompetitor(){
    this.selectedCompetitorValue = null
    let selectedArray = []
    this.competitorTargets.forEach(competitor => {
      if(competitor.checked){
        selectedArray.push(competitor.dataset.id)
      }
    })

    this.selectedCompetitorsValue = selectedArray
    this.selectedCompetitorBox.hidden = true
    this.selectedAccountBox.hidden = false
    this.updateLocation()
  }

  competitorSelect(event){
    let newArray = this.selectedCompetitorsValue
    const id = event.target.dataset.id
    if(event.target.checked){
      newArray.push(id)
    }else{
      const index = newArray.indexOf(id)
      if (index !== -1) {
        newArray.splice(index, 1)
      }
    }
    this.selectedCompetitorsValue = newArray
    this.updateLocation()
  }

  adjustCompetitor(event){
    this.selectedCompetitorValue = event.target.value
    this.selectedCompetitorsValue = [this.selectedCompetitorValue]
    this.updateLocation()
  }

  setBoundsForSelectedAccount(data){
    if(data.bounds){
      const minLat = parseFloat(data.bounds.min_lat);
      const maxLat = parseFloat(data.bounds.max_lat);
      const minLng = parseFloat(data.bounds.min_lng);
      const maxLng = parseFloat(data.bounds.max_lng);

      if (isNaN(minLat) || isNaN(maxLat) || isNaN(minLng) || isNaN(maxLng)) {
          console.error("Invalid bounds data (NaN)");
          return;
      }

      const southWest = L.latLng(minLat, minLng);
      const northEast = L.latLng(maxLat, maxLng);

      const bounds = L.latLngBounds(southWest, northEast);

      if (bounds.isValid()) {
        this.map.flyToBounds(bounds, {maxZoom: 11});
      } else {
          console.error("Invalid bounds object");
      }
    }
  }

  updateSelectedAccountUrl(){
    let url = new URL(this.selectedAccountBoxUrl.src, window.location.origin)
    let params = url.searchParams
    params.set('account_id', this.selectedAccountValue);
    this.selectedAccountBoxUrl.src = url
  }

  updateAccountList() {
    // Check for either Free View OR Training Mode
    if (this.freeFormValue || this.trainingModeValue) {
      this.accountTargets.forEach(account => {
        if(this.visibleAccountsValue.includes(Number(account.dataset.id))) {
          account.hidden = false;
        } else {
          account.hidden = true;
        }
      });
    } else {
      this.accountTargets.forEach(account => {
        account.hidden = false;
      });
    }
  }

  changeSelectedCompetitor(event){
    this.selectedCompetitorValue = event.target.value
    this.updateSelectedCompetitorUrl()
  }

  compareCompetitor(){
    this.selectedAccountBox.hidden = false
    document.querySelector('#back-btn').hidden = true
    document.querySelector('#compare-btn').hidden = true
    document.querySelector('#stop-btn').hidden = false
    document.querySelector('#selected-account-exit').hidden = true
    this.accountMap.classList.toggle('md:w-4/12')
    this.accountInfo.classList.toggle('md:w-8/12')
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      document.querySelector(`#account_lineup_btn_company_account_${this.selectedAccountValue}`).click()
    }, 600)
  }

  stopCompareCompetitor(){
    this.selectedAccountBox.hidden = true
    document.querySelector('#back-btn').hidden = false
    document.querySelector('#compare-btn').hidden = false
    document.querySelector('#stop-btn').hidden = true
    document.querySelector('#selected-account-exit').hidden = false
    this.accountMap.classList.toggle('md:w-4/12')
    this.accountInfo.classList.toggle('md:w-8/12')
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      document.querySelector(`#account_competiton_btn_company_account_${this.selectedAccountValue}`).click()
    }, 600)
  }

  updateSelectedCompetitorUrl(){
    let url = new URL(this.selectedCompetitorBoxUrl.src, window.location.origin)
    let params = url.searchParams
    params.set('account_id', this.selectedAccountValue);
    params.set('competitor_id', this.selectedCompetitorValue);
    this.selectedCompetitorBoxUrl.src = url
  }

  addAccountMarkers(locations){
    this.selectedAccountLayer.clearLayers();

    locations.forEach(location => {
      let marker = L.marker([location.latitude, location.longitude], { icon: this.orangeIcon });
      this.selectedAccountLayer.addLayer(marker);
    });
  }
}
